
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Upload, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import CertificateBackupList from './CertificateBackupList';
import { 
  createCertificateBackup, 
  importCertificateBackupFromFilesystem,
  restoreCertificateBackup
} from '@/services/backup/certificateBackup';
import { Separator } from '@/components/ui/separator';

const CertificateBackupPanel = () => {
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  
  const handleCreateBackup = async () => {
    try {
      setIsCreatingBackup(true);
      const description = `Certificate backup created on ${new Date().toLocaleDateString()}`;
      await createCertificateBackup(description);
    } catch (err) {
      console.error('Error creating certificate backup:', err);
      toast.error('Failed to create certificate backup');
    } finally {
      setIsCreatingBackup(false);
    }
  };
  
  const handleImportBackup = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    try {
      setIsImporting(true);
      const file = event.target.files[0];
      await importCertificateBackupFromFilesystem(file);
      
      // Reset the input
      event.target.value = '';
    } catch (err) {
      console.error('Error importing certificate backup:', err);
      toast.error('Failed to import certificate backup');
    } finally {
      setIsImporting(false);
    }
  };
  
  const handleRestoreBackup = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    try {
      setIsRestoring(true);
      const file = event.target.files[0];
      await restoreCertificateBackup(file);
      
      // Reset the input
      event.target.value = '';
    } catch (err) {
      console.error('Error restoring certificate backup:', err);
      toast.error('Failed to restore certificate backup');
    } finally {
      setIsRestoring(false);
    }
  };
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg">Certificate & Template Backup</CardTitle>
        <CardDescription>
          Manage backups for certificate templates and certification data
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <Button
            onClick={handleCreateBackup}
            className="w-full btn-responsive"
            disabled={isCreatingBackup}
          >
            <Save className="mr-2 h-4 w-4" />
            {isCreatingBackup ? 'Creating Backup...' : 'Create Backup'}
          </Button>
          
          <div className="relative">
            <input
              type="file"
              id="import-certificate-backup"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              onChange={handleImportBackup}
              accept=".json"
              disabled={isImporting}
            />
            <Button
              variant="outline"
              className="w-full btn-responsive"
              disabled={isImporting}
            >
              <Upload className="mr-2 h-4 w-4" />
              {isImporting ? 'Importing...' : 'Import Backup'}
            </Button>
          </div>
          
          <div className="relative">
            <input
              type="file"
              id="restore-certificate-backup"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              onChange={handleRestoreBackup}
              accept=".json"
              disabled={isRestoring}
            />
            <Button
              variant="secondary"
              className="w-full btn-responsive"
              disabled={isRestoring}
            >
              <Download className="mr-2 h-4 w-4" />
              {isRestoring ? 'Restoring...' : 'Restore From File'}
            </Button>
          </div>
        </div>
        
        <Separator className="my-4" />
        
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Recent Certificate Backups</h3>
          <CertificateBackupList />
        </div>
      </CardContent>
      
      <CardFooter className="bg-muted/50 text-xs text-muted-foreground">
        Keep your certificate templates and certification data safe with regular backups
      </CardFooter>
    </Card>
  );
};

export default CertificateBackupPanel;
